import { FC, useMemo } from 'react';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { ECardType, TCardLite } from '@xeppt/xeppt-sdk/types/card';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import classNames from 'classnames/bind';
import { ECardStatus } from '@xeppt/xeppt-sdk/types';
import { EProductName, TProduct } from '@xeppt/xeppt-sdk/types/products';
import Skeleton from '@components/common/skeleton';

interface IProps {
    onChange: (val: ECardType) => void;
    cards?: TCardLite[];
    isCardsLoading: boolean;
}

const cx = classNames.bind(styles);

const CardTypeSection: FC<IProps> = ({ cards, onChange, isCardsLoading }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'order_card.card_type'
    });
    const { account, loyalty, products } = useUserContext();
    const { submitLocale } = useLocales();
    const navigate = useNavigate();

    const isEnoughAmount = (amount: number) => {
        return (account?.balance || 0) < amount;
    };

    const currentProducts = useMemo(() => {
        const productsResult = products?.filter((item) => item.level === loyalty?.level);
        const result: {
            primary: Omit<TProduct, 'userType'> | undefined;
            supplementary: Omit<TProduct, 'userType'> | undefined;
        } = {
            primary: undefined,
            supplementary: undefined
        };
        if (!cards) return result;
        if (cards.find((item) => item.type === ECardType.PHYSICAL)) {
            result.primary = productsResult?.find(
                (item) => item.name === EProductName.OrderPhysicCardNextTimes
            );
        } else {
            result.primary = productsResult?.find(
                (item) => item.name === EProductName.OrderPhysicCardFirstTime
            );
        }
        if (cards.find((item) => item.type === ECardType.PHYSICAL_SUPPLEMENTARY)) {
            result.supplementary = productsResult?.find(
                (item) => item.name === EProductName.OrderSupplementaryPhysicCardNextTimes
            );
        } else {
            result.supplementary = productsResult?.find(
                (item) => item.name === EProductName.OrderSupplementaryPhysicCardFirstTime
            );
        }
        return result;
    }, [cards, products, loyalty]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.card}>
                <div className={styles.content_wrapper}>
                    <Typography className={styles.title} variant="h4">
                        {t('physical_card')}
                    </Typography>
                    <Typography className={styles.description} variant="body3">
                        {t('physical_description')}
                    </Typography>
                    <div className={styles.shipping}>
                        <Icon name="delivery" width={20} height={20} />
                        {t('physical_get')}
                    </div>
                    <div className={styles.tag}>
                        {isCardsLoading || !cards ? (
                            <Skeleton className={styles.price_skeleton} />
                        ) : (
                            <>
                                {currentProducts?.primary?.price === 0
                                    ? 'Free'
                                    : '$' + (currentProducts?.primary?.price || 0)?.toFixed(2)}
                            </>
                        )}
                    </div>
                    {isEnoughAmount(currentProducts?.primary?.price || 0) && (
                        <Typography variant="body3" className={styles.helper_text}>
                            {t('not_enough_balance')}
                        </Typography>
                    )}
                </div>
                <Button
                    variant="primary"
                    onClick={() => {
                        if (isEnoughAmount(currentProducts?.primary?.price || 0)) {
                            navigate(routes.send_and_request_request);
                        } else {
                            onChange(ECardType.PHYSICAL);
                        }
                    }}>
                    {isEnoughAmount(currentProducts?.primary?.price || 0)
                        ? submitLocale('load_balance')
                        : 'Order'}
                </Button>
            </div>
            <div className={cx([styles.card])}>
                <div className={styles.content_wrapper}>
                    <Typography className={styles.title} variant="h4">
                        {t('supplementary_card')}
                    </Typography>
                    <Typography className={styles.description} variant="body3">
                        {t('supplementary_description')}
                    </Typography>
                    <div className={styles.shipping}>
                        <Icon name="delivery" width={20} height={20} />
                        {t('supplementary_get')}
                    </div>
                    <div className={styles.tag}>
                        {isCardsLoading || !cards ? (
                            <Skeleton className={styles.price_skeleton} />
                        ) : (
                            <>
                                {currentProducts?.supplementary?.price === 0
                                    ? 'Free'
                                    : '$' +
                                      (currentProducts?.supplementary?.price || 0)?.toFixed(2)}
                            </>
                        )}
                    </div>
                    {isEnoughAmount(currentProducts?.supplementary?.price || 0) && (
                        <Typography variant="body3" className={styles.helper_text}>
                            {t('not_enough_balance')}
                        </Typography>
                    )}
                </div>
                <Button
                    variant="primary"
                    disabled={
                        (cards || [])?.length === 0 ||
                        !cards?.find(
                            (item) =>
                                item.type === ECardType.PHYSICAL &&
                                item.status === ECardStatus.ACTIVE
                        )
                    }
                    onClick={() => {
                        if (isEnoughAmount(currentProducts?.primary?.price || 0)) {
                            navigate(routes.send_and_request_request);
                        } else {
                            onChange(ECardType.PHYSICAL_SUPPLEMENTARY);
                        }
                    }}>
                    {isEnoughAmount(currentProducts?.primary?.price || 0)
                        ? submitLocale('load_balance')
                        : 'Order'}
                </Button>
            </div>
        </div>
    );
};

export default CardTypeSection;
